import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "@/assets/css/style.css";
import { useAppSelector } from "@/store/hooks";

interface Category {
  _id: string;
  name: string;
}

interface Tag {
  _id: string;
  name: string;
}

const BlogForm: React.FC = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { token } = useAppSelector((state) => state.auth);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [tagsId, setTagsId] = useState("");
  const [status, setStatus] = useState(true);

  // Image upload state
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");

  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch categories
  useEffect(() => {
    fetch(`${baseUrl}/categories`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) setCategories(data.data);
      });
  }, [baseUrl, token]);

  // Fetch tags
  useEffect(() => {
    fetch(`${baseUrl}/tags`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) setTags(data.data);
      });
  }, [baseUrl, token]);

  // Fetch blog details if editing
  useEffect(() => {
    if (id) {
      fetch(`${baseUrl}/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status && data.data) {
            const blog = data.data;
            setTitle(blog.title || "");
            setSlug(blog.slug || "");
            setDescription(blog.description || "");
            setCategoryId(blog.categories?.[0]?._id || "");
            setTagsId(blog.tags?.[0]?._id || "");
            setStatus(blog.status);

            // Pre-fill image preview
            if (blog.image) setPreviewUrl(blog.image);
          }
        });
    }
  }, [id, baseUrl, token]);

  // Preview selected image
  useEffect(() => {
    if (image) {
      const url = URL.createObjectURL(image);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [image]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setImage(e.target.files[0]);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("slug", slug.trim());
      formData.append("description", description.trim());
      formData.append("categories", categoryId);
      formData.append("tags", tagsId);
      formData.append("status", status ? "true" : "false");
      if (image) formData.append("image", image);

      const url = id ? `${baseUrl}/blogs/${id}` : `${baseUrl}/blogs`;
      const method = id ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.status) {
        alert(id ? "Blog updated!" : "Blog added!");
        navigate("/blogs");
      } else {
        alert(data.message || "Something went wrong!");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="side-app">
      <div className="main-container container-fluid">
        <div className="page-header">
          <h1 className="page-title">{id ? "Edit Blog" : "Add Blog"}</h1>
        </div>

        <form className="card" onSubmit={handleSubmit} encType="multipart/form-data">
          <div className="card-body">
            {/* Title */}
            <div className="form-group">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            {/* Slug */}
            <div className="form-group">
              <label className="form-label">Slug</label>
              <input
                type="text"
                className="form-control"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
              />
            </div>

            {/* Category */}
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                required
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="form-group">
              <label className="form-label">Tags</label>
              <select
                className="form-select"
                value={tagsId}
                onChange={(e) => setTagsId(e.target.value)}
                required
              >
                <option value="">Select Tag</option>
                {tags.map((tag) => (
                  <option key={tag._id} value={tag._id}>{tag.name}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Image Upload */}
            <div
              className="upload-box mb-4"
              onClick={() => document.getElementById("fileInput")?.click()}
              style={{
                border: "2px dashed #bbb",
                borderRadius: "12px",
                padding: "30px",
                textAlign: "center",
                cursor: "pointer",
                background: "#fafafa",
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files[0]) setImage(e.dataTransfer.files[0]);
              }}
            >
              {previewUrl ? (
                <div>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="img-fluid rounded shadow"
                    style={{ maxHeight: "200px", objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger mt-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImage(null);
                      setPreviewUrl("");
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <p className="fw-bold mb-1">Drag & Drop your image here</p>
                  <p className="text-muted mb-0">or click to browse</p>
                  <small className="d-block mt-2">Recommended: 150 Px × 150 Px</small>
                </>
              )}
              <input
                id="fileInput"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => e.target.files && setImage(e.target.files[0])}
                required={!id}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (id ? "Updating…" : "Uploading…") : id ? "Update" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogForm;
